import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import Button, { ButtonProps } from '@pelckmans/elementis/components/button';
import { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../contexts/ModalContext';
import AddPersonalGroupModal from '../modals/add-personal-group/AddPersonalGroupModal';
import { SchoolYear } from '../context/MyGroupsContext';
import AccountContext from '../../../contexts/AccountContext';
import { Group } from '../../../api/groups';
import ImportSmartschoolGroupModal from '../modals/import-smartschool-group/ImportSmartschoolGroupModal';
import useGroups from '../hooks/useGroups';
import useStudioGroup from '../../../hooks/useStudioGroup';
import PlusIcon from '@pelckmans/elementis/icons/plus';
import AddVoGroupModal from '../modals/code-based/add-group/AddGroupModal';

type Props = {
  schoolYear: SchoolYear;
  onGroupsChange: Dispatch<SetStateAction<Group[] | undefined>>;
  size: ButtonProps['size'];
};

function AddGroupButton({ schoolYear, onGroupsChange, size }: Props) {
  const { t } = useTranslation();
  const studioGroup = useStudioGroup();
  const { fetchGroups } = useGroups();

  const {
    account: { oauthIdentities },
  } = useContext(AccountContext);
  const { groupsWithSchool } = useGroups();

  const hasCoupledSmartschoolAccount = useMemo(() => {
    const hasSmartschoolIdentity = Boolean(oauthIdentities?.some(x => x.provider === 'smartschool'));

    return ['SO', 'ES'].includes(process.env.REACT_APP_P_STUDIO_GROUP) && hasSmartschoolIdentity;
  }, [oauthIdentities]);

  const { showModal } = useContext(ModalContext);

  const openAddGroupModal = () => {
    if (studioGroup === 'VO' || studioGroup === 'BAO') {
      showModal(
        <AddVoGroupModal
          id="add-code-based-group-modal"
          selectedSchoolYear={schoolYear}
          onGroupAdded={() => fetchGroups(schoolYear)}
        />,
      );
    } else {
      showModal(
        <AddPersonalGroupModal id="add-personal-group-modal" schoolYear={schoolYear} onGroupsChange={onGroupsChange} />,
      );
    }
  };

  const openImportSmartschoolGroupModal = () => {
    showModal(
      <ImportSmartschoolGroupModal
        id="import-smartschool-group-modal"
        schoolYear={schoolYear}
        groupsWithSchool={groupsWithSchool}
        onGroupsChange={onGroupsChange}
      />,
    );
  };

  if (hasCoupledSmartschoolAccount) {
    return (
      <ButtonDropdown
        buttonProps={{ prominence: 'secondary' }}
        title={t('PAGES.MY_GROUPS.ADD_GROUP.BUTTON_DROPDOWN.TITLE')}
        size={size}
        disableBackgroundScroll
      >
        <ButtonDropdown.Item
          onClick={openImportSmartschoolGroupModal}
          value="smartschool"
          text={t('PAGES.MY_GROUPS.ADD_GROUP.BUTTON_DROPDOWN.ITEMS.SMARTSCHOOL_GROUP')}
        />
        <ButtonDropdown.Item
          onClick={openAddGroupModal}
          value="personal"
          text={t('PAGES.MY_GROUPS.ADD_GROUP.BUTTON_DROPDOWN.ITEMS.PERSONAL_GROUP')}
        />
      </ButtonDropdown>
    );
  }

  return (
    <Button
      onClick={openAddGroupModal}
      prominence="secondary"
      size={size}
      startIcon={studioGroup === 'VO' ? <PlusIcon /> : undefined}
    >
      {t('PAGES.MY_GROUPS.ADD_GROUP.BUTTON_DROPDOWN.TITLE')}
    </Button>
  );
}

export default AddGroupButton;
